'use client';

import React, { useState } from 'react';
import { Link as LinkIcon, Check } from 'lucide-react';
import { XIcon } from '@/components/icons/SocialIcons';
import { trackShare } from '@/lib/analytics';

interface SocialShareProps {
  url: string;
  title: string;
  contentType?: 'article' | 'coin' | 'nft';
  className?: string;
  variant?: 'inline' | 'floating';
}

export default function SocialShare({
  url,
  title,
  contentType = 'article',
  className = '',
  variant = 'inline',
}: SocialShareProps) {
  const [copied, setCopied] = useState(false);

  const shareOnTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}&via=descoutxyz`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    
    // Track the share event
    trackShare({
      platform: 'twitter',
      contentType,
      contentTitle: title,
      contentUrl: url,
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      
      // Track the copy event
      trackShare({
        platform: 'copy_link',
        contentType,
        contentTitle: title,
        contentUrl: url,
      });
      
      // Reset after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  if (variant === 'floating') {
    return (
      <div className={`fixed right-4 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3 ${className}`}>
        <button
          onClick={shareOnTwitter}
          className="w-12 h-12 bg-black dark:bg-white text-white dark:text-black rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-lg"
          aria-label="Share on X"
          title="Share on X"
        >
          <XIcon className="w-5 h-5" />
        </button>
        
        <button
          onClick={copyLink}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
            copied
              ? 'bg-green-500 text-white'
              : 'bg-gray-200 dark:bg-neutral-800 text-gray-700 dark:text-gray-200 hover:scale-110'
          }`}
          aria-label="Copy link"
          title="Copy link"
        >
          {copied ? <Check className="w-5 h-5" /> : <LinkIcon className="w-5 h-5" />}
        </button>
      </div>
    );
  }

  // Inline variant
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Share:</span>
      
      <button
        onClick={shareOnTwitter}
        className="inline-flex items-center gap-2 px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:opacity-90 transition-opacity"
        aria-label="Share on X"
      >
        <XIcon className="w-4 h-4" />
        <span className="text-sm font-medium">X</span>
      </button>
      
      <button
        onClick={copyLink}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
          copied
            ? 'bg-green-500 text-white'
            : 'bg-gray-200 dark:bg-neutral-800 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-slate-600'
        }`}
        aria-label="Copy link"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4" />
            <span className="text-sm font-medium">Copied!</span>
          </>
        ) : (
          <>
            <LinkIcon className="w-4 h-4" />
            <span className="text-sm font-medium">Copy Link</span>
          </>
        )}
      </button>
    </div>
  );
}


